import React from "react";
import { Text, Flex } from "@jsq/ds/core";
import { DetailedWirePaymentPrefObjectType } from "../../../common/types";
import { BankAccountDomicile } from "../BankAccountDomicile";
import FormattedAddress from "../FormattedAddress";
import PaymentDetailsModalField from "../PaymentDetailsModalField";
import { WireVisibleFields } from "../types";
import { getAddress } from "../utils";
import { isWireFieldVisible } from "../utils/fieldVisibility";
import { WireInfoField } from "./WireInfoField";

export type WirePaymentPrefDisplayProps = {
  wirePaymentPrefFull: DetailedWirePaymentPrefObjectType;
  visibleFields?: WireVisibleFields;
  showBankDomicile?: boolean;
};

export const WirePaymentPrefDisplay: React.FC<WirePaymentPrefDisplayProps> = ({
  wirePaymentPrefFull,
  visibleFields,
  showBankDomicile,
}: WirePaymentPrefDisplayProps) => {
  const obiInfo = WireInfoField({ wireInfo: wirePaymentPrefFull.wireObi });
  const bbiInfo = WireInfoField({ wireInfo: wirePaymentPrefFull.wireBbi });
  const beneficiaryAddress =
    wirePaymentPrefFull.beneficiaryAddress &&
    getAddress(wirePaymentPrefFull.beneficiaryAddress);
  const bankAddress =
    wirePaymentPrefFull.bank?.address && getAddress(wirePaymentPrefFull.bank.address);
  const intermediaryBankAddress =
    wirePaymentPrefFull.intermediaryBank?.address &&
    getAddress(wirePaymentPrefFull.intermediaryBank.address);

  const gap = 8; // gap between rows and columns

  return (
    <Flex gap={gap} flexDirection="column" data-testid="WirePaymentPrefDisplayFlex">
      <PaymentDetailsModalField
        label="Account holder name"
        value={wirePaymentPrefFull.beneficiaryName}
        gap={gap}
        visible={isWireFieldVisible("beneficiaryName", visibleFields)}
      />
      <PaymentDetailsModalField
        label="Bank name"
        value={wirePaymentPrefFull.bank?.name}
        gap={gap}
        visible={isWireFieldVisible("bank", visibleFields)}
      />
      {wirePaymentPrefFull.bank?.abaNumber && (
        <PaymentDetailsModalField
          label="ABA routing number"
          value={wirePaymentPrefFull.bank.abaNumber}
          gap={gap}
          visible={isWireFieldVisible("bank", visibleFields)}
        />
      )}
      {wirePaymentPrefFull.bank?.swiftCode && (
        <PaymentDetailsModalField
          label="SWIFT / BIC code"
          value={wirePaymentPrefFull.bank.swiftCode}
          gap={gap}
          visible={isWireFieldVisible("bank", visibleFields)}
        />
      )}
      {wirePaymentPrefFull.bank?.intlRoutingCode && (
        <PaymentDetailsModalField
          label="International routing code"
          value={wirePaymentPrefFull.bank.intlRoutingCode}
          gap={gap}
          visible={isWireFieldVisible("bank", visibleFields)}
        />
      )}
      <PaymentDetailsModalField
        label="Account number"
        value={wirePaymentPrefFull.beneficiaryAccountNumber}
        gap={gap}
        visible={isWireFieldVisible("beneficiaryAccountNumber", visibleFields)}
      />
      {isWireFieldVisible("wireObi", visibleFields) && obiInfo && (
        <Flex gap={gap}>
          <Flex flexBasis="60%">
            <Text variant="secondary-md">OBI</Text>
          </Flex>
          <Flex flexBasis="100%" flexDirection="column">
            {obiInfo}
          </Flex>
        </Flex>
      )}
      {wirePaymentPrefFull.referenceToBeneficiary && (
        <PaymentDetailsModalField
          label="Reference to beneficiary"
          value={wirePaymentPrefFull.referenceToBeneficiary}
          gap={gap}
          visible={isWireFieldVisible("referenceToBeneficiary", visibleFields)}
        />
      )}
      {isWireFieldVisible("wireBbi", visibleFields) && bbiInfo && (
        <Flex gap={gap}>
          <Flex flexBasis="60%">
            <Text variant="secondary-md">BBI</Text>
          </Flex>
          <Flex flexBasis="100%" flexDirection="column">
            {bbiInfo}
          </Flex>
        </Flex>
      )}
      {isWireFieldVisible("beneficiaryAddress", visibleFields) && (
        <Flex gap={gap}>
          <Flex flexBasis="60%">
            <Text variant="secondary-md">Account holder address</Text>
          </Flex>
          <Flex flexBasis="100%" flexDirection="column">
            {beneficiaryAddress && (
              <FormattedAddress address={beneficiaryAddress} variant="regular-md" />
            )}
          </Flex>
        </Flex>
      )}
      {isWireFieldVisible("bank", visibleFields) && (
        <Flex gap={gap}>
          <Flex flexBasis="60%">
            <Text variant="secondary-md">Bank address</Text>
          </Flex>
          <Flex flexBasis="100%" flexDirection="column">
            {bankAddress && <FormattedAddress address={bankAddress} variant="regular-md" />}
          </Flex>
        </Flex>
      )}
      {showBankDomicile && (
        <PaymentDetailsModalField
          label="Bank account domicile"
          value={
            <BankAccountDomicile
              countryName={wirePaymentPrefFull.bank?.address?.country?.name}
            />
          }
          gap={gap}
          visible={!!wirePaymentPrefFull.bank?.address?.country?.name}
        />
      )}
      {isWireFieldVisible("intermediaryBank", visibleFields) &&
        wirePaymentPrefFull.intermediaryBank && (
          <>
            <PaymentDetailsModalField
              label="Intermediary bank"
              value={wirePaymentPrefFull.intermediaryBank.name}
              gap={gap}
              visible={isWireFieldVisible("intermediaryBank", visibleFields)}
            />
            {wirePaymentPrefFull.intermediaryBank.abaNumber && (
              <PaymentDetailsModalField
                label="Intermediary bank ABA routing number"
                value={wirePaymentPrefFull.intermediaryBank.abaNumber}
                gap={gap}
                visible={isWireFieldVisible("intermediaryBank", visibleFields)}
              />
            )}
            {wirePaymentPrefFull.intermediaryBank.swiftCode && (
              <PaymentDetailsModalField
                label="Intermediary bank SWIFT / BIC code"
                value={wirePaymentPrefFull.intermediaryBank.swiftCode}
                gap={gap}
                visible={isWireFieldVisible("intermediaryBank", visibleFields)}
              />
            )}
            {wirePaymentPrefFull.intermediaryBank.intlRoutingCode && (
              <PaymentDetailsModalField
                label="Intermediary bank international routing code"
                value={wirePaymentPrefFull.intermediaryBank.intlRoutingCode}
                gap={gap}
                visible={isWireFieldVisible("intermediaryBank", visibleFields)}
              />
            )}
            <Flex gap={gap}>
              <Flex flexBasis="60%">
                <Text variant="secondary-md">Intermediary bank address</Text>
              </Flex>
              <Flex flexBasis="100%" flexDirection="column">
                {intermediaryBankAddress && (
                  <FormattedAddress address={intermediaryBankAddress} variant="regular-md" />
                )}
              </Flex>
            </Flex>
          </>
        )}
    </Flex>
  );
};
